import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, Select, Tag, LocaleText, Message } from 'ui';
import { useT } from 'hooks/Locale/useT';
import { Table, message } from 'antd';
import moment from 'moment';
import { config } from 'config/config';
import { TagStatus } from 'ui/Tag/types';
import useQueryApiClient from 'utils/useApiClient';

interface ActivitiesChangeModalInterface {
    open: boolean;
    onCancel: React.Dispatch<React.SetStateAction<boolean>>;
    id: number;
    activityId: number;
    onSuccess?: () => void;
}

const ActivitiesChangeModal: React.FC<ActivitiesChangeModalInterface> = ({
    open,
    onCancel,
    id,
    activityId,
    onSuccess,
}) => {
    const t = useT();
    const [attendances, setAttendances] = useState([]);
    const [attempts, setAttempts] = useState([]);
    const [selectedAttempts, setSelectedAttempts] = useState<Record<number, any[]>>({});

    const prevOpenRef = useRef(open);

    useEffect(() => {
        if (open && !prevOpenRef.current) {
            fetch();
            fetchAttempts();
        }
        prevOpenRef.current = open;
    }, [open]);

    const { fetch, isLoading } = useQueryApiClient({
        request: {
            disableOnMount: true,
            url: `/api/v1/courses/learning-program/results/${id}/activities/${activityId}`,
            method: 'GET',
        },
        onSuccess: (response) => {
            setAttendances(response.data);
        },
    });

    const { fetch: fetchAttempts } = useQueryApiClient({
        request: {
            disableOnMount: true,
            url: `/api/v1/courses/learning-program/results/${id}/attempts`,
            method: 'GET',
        },
        onSuccess: (response) => {
            setAttempts(response.data);
        },
    });

    const { appendData, isLoading: isSaveLoading } = useQueryApiClient({
        request: {
            disableOnMount: true,
            url: `/api/v1/courses/learning-program/results/${id}/activities`,
            method: 'POST',
        },
        onSuccess: () => {
            message.success(t('general.success'));
            if (onSuccess) {
                onSuccess();
            }
        },
    });

    const { appendData: deleteActivity, isLoading: isDeleteLoading } = useQueryApiClient({
        request: {
            disableOnMount: true,
            url: `/api/v1/courses/learning-program/results/:id/activities/:activityId/delete`,
            method: 'DELETE',
        },
        onSuccess: () => {
            message.success(t('general.deletion_successful'));
            fetch();
        },
    });

    const { appendData: restoreActivity, isLoading: isRestoreLoading } = useQueryApiClient({
        request: {
            disableOnMount: true,
            url: `/api/v1/courses/learning-program/results/:id/activities/:activityId/restore`,
            method: 'POST',
        },
        onSuccess: () => {
            message.success(t('general.success'));
            fetch();
        },
    });

    const handleCancel = () => {
        onCancel(false);
        setAttendances([]);
        setAttempts([]);
        setSelectedAttempts({});
    };

    const handleSelectChange = (recordId: number, values: any[]) => {
        setSelectedAttempts((prev) => ({
            ...prev,
            [recordId]: values,
        }));
    };

    const handleSaveAttempts = (recordId: number) => {
        const selected = selectedAttempts[recordId];
        if (selected) {
            appendData({
                activityId: recordId,
                attempt: selected,
            });
        }
    };

    const columns = [
        {
            title: t('general.title'),
            dataIndex: 'activity_title',
            render: (text: string) => <LocaleText text={text} />,
        },
        {
            title: t('general.status'),
            dataIndex: 'status',
            render: (status: TagStatus) => (status ? <Tag status={status} /> : null),
        },
        {
            title: t('general.is_completed'),
            dataIndex: 'finished_at',
            render: (date: string) =>
                date ? moment(date).format(config.defaultDateFormat + ' HH:mm') : '-',
        },
        {
            title: t('program.attempt'),
            render: (record: any) => {
                return (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Select
                            defaultValue={record.attempt_id + ''}
                            value={selectedAttempts[record.id] ?? record.attempts}
                            isFormItem={false}
                            manualOptions={attempts}
                            onChange={(values) => handleSelectChange(record.id, values)}
                            style={{ width: 100 }}
                        />
                        <Button
                            onClick={() => handleSaveAttempts(record.id)}
                            loading={isSaveLoading}
                            message="general.save"
                        />
                    </div>
                );
            },
        },
        {
            title: t('general.deleted_at'),
            dataIndex: 'deleted_at',
            render: (date: string) =>
                date ? moment(date).format(config.defaultDateFormat + ' HH:mm') : '-',
        },
        {
            render: (record: any) => (
                <div>
                    {record.deleted_at ? (
                        <Button
                            loading={isRestoreLoading}
                            onClick={() => restoreActivity({}, { id: id, activityId: record.id })}
                            message="general.restore"
                        />
                    ) : (
                        <Button
                            loading={isDeleteLoading}
                            onClick={() => deleteActivity({}, { id: id, activityId: record.id })}
                            message="general.delete"
                        />
                    )}
                </div>
            ),
        },
    ];

    return (
        <Modal
            open={open}
            width={'80%'}
            onCancel={handleCancel}
            className="add_remove_activity"
            title={t('program.activities_update_modal')}
            destroyOnClose={true}
            footer={<Button onClick={handleCancel} message="general.close" />}
        >
            <Table
                loading={isLoading}
                columns={columns}
                dataSource={attendances}
                rowKey={'id'}
                pagination={{ position: [] }}
            />
        </Modal>
    );
};

export default ActivitiesChangeModal;
